/**
 * SubmittedRequestSection — the request the AM actually launched, with its
 * declared values and (host permitting) a reuse control.
 *
 * Visual contract: RunningDrawer.dc.html `POLICY · SUBMITTED` — collapsed to
 * a summary; expands to every field with a copy button plus Copy all fields
 * (the values pasted into the carrier portal while Epic runs).
 *
 * FIXED field set: every declared field always has a row, so body height does
 * not change as the payload shape varies. That is the calm-UI no-jitter rule
 * `RunSection`'s fixed six-row field set was introduced to enforce.
 *
 * The section performs NO transport. It cannot: it is handed values and a
 * callback. It never cancels, never relaunches, never mutates the viewed
 * instance (AC-9).
 *
 * `onReuse` absent ⇒ NO reuse control (AD-PLAN-7). Ora has no mortgagee launch
 * host today, and a button that opens nothing is worse than no button.
 */

import { useEffect, useId, useRef, useState } from "react";
import CustomTooltip from "~/common/components/CustomTooltip";
import {
  NOT_SUPPLIED,
  type SubmittedField,
  type SubmittedRequestModel,
} from "../../submittedRequest";

export const SUBMITTED_REQUEST_TITLE = "POLICY · SUBMITTED";

export interface SubmittedRequestSectionProps {
  model: SubmittedRequestModel;
  /** When present — and ONLY then — a reuse control is rendered. */
  onReuse?: (
    values: Record<string, unknown>,
    identity: string | null,
  ) => void;
  defaultExpanded?: boolean;
  persistKey?: string;
}

/** Values reach the surface as whatever the payload declared. Rendering is
 *  lossless and never reformats: a reformatted date is a value the payload
 *  does not carry. */
function displayValue(value: unknown): string {
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  if (Array.isArray(value)) {
    return value
      .map((v) =>
        v && typeof v === "object"
          ? Object.values(v as Record<string, unknown>)
              .filter((x) => typeof x === "string" || typeof x === "number")
              .join(" · ")
          : String(v),
      )
      .join("; ");
  }
  if (value && typeof value === "object") {
    return Object.values(value as Record<string, unknown>)
      .filter((x) => typeof x === "string" || typeof x === "number")
      .join(" · ");
  }
  return NOT_SUPPLIED;
}

function fieldText(f: SubmittedField): string {
  return f.supplied ? displayValue(f.value) : NOT_SUPPLIED;
}

function fieldByKey(model: SubmittedRequestModel, key: string): SubmittedField | undefined {
  return model.fields.find((f) => f.key === key);
}

function summaryLines(model: SubmittedRequestModel): { title: string; line2: string; line3: string } {
  const num = fieldByKey(model, "policy_number");
  const termA = fieldByKey(model, "term_effective_date");
  const termB = fieldByKey(model, "term_expiration_date");
  const ptype = fieldByKey(model, "policy_type_code");
  const change = fieldByKey(model, "change_type");
  const itype = fieldByKey(model, "interest_type");
  const eff = fieldByKey(model, "effective_date");

  const titleParts = [
    num?.supplied ? displayValue(num.value) : null,
    termA?.supplied && termB?.supplied
      ? `${displayValue(termA.value)} – ${displayValue(termB.value)}`
      : termA?.supplied
        ? displayValue(termA.value)
        : null,
  ].filter(Boolean);
  const line2Parts = [ptype?.supplied ? displayValue(ptype.value) : null].filter(Boolean);
  const line3Parts = [
    eff?.supplied ? `eff ${displayValue(eff.value)}` : null,
    change?.supplied ? displayValue(change.value) : null,
    itype?.supplied ? displayValue(itype.value) : null,
  ].filter(Boolean);

  return {
    title: titleParts.join(" · ") || "Submitted request",
    line2: line2Parts.join(" — "),
    line3: line3Parts.join(" · "),
  };
}

function copyText(text: string) {
  try {
    void navigator.clipboard?.writeText(text);
  } catch {
    /* jsdom / denied */
  }
}

function CopyButton({
  copied,
  onCopy,
}: {
  copied: boolean;
  onCopy: () => void;
}) {
  // "Copy" in ora's tooltip, not a native title (story 28633). Node title +
  // describeChild: the sr-only line stays the name, so "Copied" is still
  // announced after a copy.
  const tooltipId = `submitted-request-copy-${useId().replace(/:/g, "")}`;
  return (
    <CustomTooltip
      id={tooltipId}
      title={<span>Copy</span>}
      describeChild
      placement="top"
      arrow
      isOnModal
    >
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onCopy();
        }}
        className={`flex h-6 w-[26px] flex-shrink-0 items-center justify-center rounded border ${
          copied
            ? "border-primary-success-200 bg-primary-success-50"
            : "border-primary-gray-light-200 bg-base-white"
        }`}
      >
        {copied ? (
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="m5 13 4 4L19 7"
              stroke="#067647"
              strokeWidth="2.5"
              strokeLinecap="round"
            />
          </svg>
        ) : (
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <rect x="9" y="9" width="12" height="12" rx="2" stroke="#667085" strokeWidth="2" />
            <path
              d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"
              stroke="#667085"
              strokeWidth="2"
            />
          </svg>
        )}
        <span className="sr-only">{copied ? "Copied" : "Copy"}</span>
      </button>
    </CustomTooltip>
  );
}

export function SubmittedRequestSection({
  model,
  onReuse,
  defaultExpanded = false,
}: SubmittedRequestSectionProps) {
  const [open, setOpen] = useState(defaultExpanded);
  const [copied, setCopied] = useState<string | null>(null);
  const copiedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (copiedTimer.current) clearTimeout(copiedTimer.current);
    },
    [],
  );

  const markCopied = (key: string, text: string) => {
    copyText(text);
    setCopied(key);
    if (copiedTimer.current) clearTimeout(copiedTimer.current);
    copiedTimer.current = setTimeout(
      () => setCopied((cur) => (cur === key ? null : cur)),
      1600,
    );
  };

  const summary = summaryLines(model);
  const draftLabel =
    model.draftReply === null ? NOT_SUPPLIED : model.draftReply ? "On" : "Off";
  const allFieldsText = [
    ...model.fields.map((f) => `${f.label}: ${fieldText(f)}`),
    `Reply draft: ${draftLabel}`,
  ].join("\n");

  return (
    <div
      className="mb-2 flex flex-col gap-2.5 rounded-lg border border-primary-gray-light-300 p-3"
      data-testid="submitted-request"
    >
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 text-left"
        data-testid="submitted-request-toggle"
      >
        <span className="flex-1 text-2xs font-semibold uppercase tracking-wide text-primary-gray-light-500">
          {SUBMITTED_REQUEST_TITLE}
        </span>
        <span className="text-xs text-primary-gray-light-400">
          {open ? "Hide" : "All fields"}
        </span>
        <span
          className="text-primary-gray-light-400 text-xs leading-none transition-transform duration-150"
          aria-hidden="true"
          style={{ transform: open ? "rotate(90deg)" : "rotate(0deg)" }}
        >
          ›
        </span>
      </button>

      {model.shape === "legacy_flat" && (
        <p
          className="text-2xs text-primary-gray-light-500"
          data-testid="submitted-request-legacy-shape"
        >
          Launched under the earlier request shape.
        </p>
      )}

      {!open && (
        <div className="flex flex-col gap-0.5">
          <p className="text-sm font-semibold leading-5 text-primary-gray-light-900">
            {summary.title}
          </p>
          {summary.line2 ? (
            <p className="text-xs text-primary-gray-light-700">{summary.line2}</p>
          ) : null}
          {summary.line3 ? (
            <p className="text-xs text-primary-gray-light-400">{summary.line3}</p>
          ) : null}
        </div>
      )}

      {open && (
        <>
          <div className="flex flex-col">
            {model.fields.map((f) => {
              const value = fieldText(f);
              return (
                <div
                  key={f.key}
                  className="flex items-start gap-2.5 border-b border-primary-gray-light-100 py-2"
                >
                  <span className="w-24 flex-shrink-0 text-xs text-primary-gray-light-700">
                    {f.label}:
                  </span>
                  <span className="min-w-0 flex-1 break-words text-xs text-primary-gray-light-900">
                    {value}
                  </span>
                  <CopyButton
                    copied={copied === f.key}
                    onCopy={() => markCopied(f.key, value)}
                  />
                </div>
              );
            })}
            <div className="flex items-start gap-2.5 py-2">
              <span className="w-24 flex-shrink-0 text-xs text-primary-gray-light-700">
                Reply draft:
              </span>
              <span className="min-w-0 flex-1 text-xs text-primary-gray-light-900">
                {draftLabel}
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={() => markCopied("__all", allFieldsText)}
            className="flex items-center justify-center rounded-md border border-primary-gray-light-300 px-2 py-2 text-xs font-semibold text-primary-gray-light-800"
            data-testid="submitted-request-copy-all"
          >
            {copied === "__all" ? "Copied all fields" : "Copy all fields"}
          </button>
        </>
      )}

      {onReuse && (
        <button
          type="button"
          onClick={() => onReuse(model.reuseValues, model.identity)}
          className="self-start rounded border border-primary-gray-light-300 bg-base-white px-2 py-0.5 text-2xs font-semibold text-primary-gray-light-800 hover:bg-primary-gray-light-50"
          data-testid="submitted-request-reuse"
        >
          Reuse these values
        </button>
      )}
    </div>
  );
}

export default SubmittedRequestSection;
